import { Dialog, DialogContent, DialogTitle } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { t } from 'src/lib/language';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { DIALOGS } from 'src/models/DialogModel';
import { closeDialog } from 'src/redux/actions/dialogActions';
import { putPaymentAction } from 'src/redux/actions/paymentActions';
import { getEntitiesAction } from 'src/redux/actions/querybuilderActions';
import { PAYMENT_STATUS, PAYMENT_TYPE } from 'src/models/PaymentModel';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment-timezone-all';
import Row from 'src/components/UI/Row';
import BottomRow from 'src/components/UI/BottomRow';
import Form from 'src/components/UI/Form';
import { MODALS_SIZE } from 'src/models/modal';
import { CreditCardModel, CreditCardPayload } from 'src/models/CreditCardModel';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import { FORM } from 'src/constants/Form';
import CreditCardValidator from 'src/validations/CreditCardValidator';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { ErrorBag } from 'src/models/ErrorModel';
import { setError } from 'src/redux/actions/errorsActions';
import { getCardType } from 'src/lib/PaymentHelper';
import { putContactCreditCardAction, getUserCreditCardAction } from 'src/redux/actions/paymentInfoAction';
import { useEffect, useState } from 'react';
import { ENTITIES } from 'src/models/QuerybuilderModel';

const RetryUserPaymentDialog = () => {
  const dispatch = useAppDispatch();

  const opened =
    useSelector((state: Store) => state.dialog[DIALOGS.retryUserPayment]?.opened || false);
  const payment =
    useSelector((state: Store) => state.dialog[DIALOGS.retryUserPayment]?.payload);

  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const contactId = useSelector((state: Store) => state.user.organisation_contact_id);

  const creditCards = useSelector(
    (state: Store) => (state.userCreditCards as CreditCardModel[]) || [],
  );

  const creditCard = creditCards.find(
    (cc: CreditCardModel) => cc.id === payment?.payment_info_id,
  );

  const [editCard, setEditCard] = useState(false);

  const getExpirationParts = () => {
    if (!creditCard?.expiration_data) return { month: '', year: '' };
    const parts = creditCard.expiration_data.split('/');
    return { month: parts[0] || '', year: parts[1] || '' };
  };

  const getCardHolderParts = () => {
    if (!creditCard?.card_holder) return { first: '', last: '' };
    const parts = creditCard.card_holder.split(' ');
    return { first: parts[0] || '', last: parts.slice(1).join(' ') || '' };
  };

  const { month: expMonth, year: expYear } = getExpirationParts();
  const { first, last } = getCardHolderParts();

  const [cardState, setCardState] = useState<CreditCardPayload>({
    name: creditCard?.name || '',
    first_name: first,
    last_name: last,
    card_number: '',
    expiration_month: expMonth,
    expiration_year: expYear,
    cvv: '',
    card_type: '',
  });

  useEffect(() => {
    if (!opened) return;
    // Reset card edit state when dialog opens
    setEditCard(false);
    setCardState({
      name: creditCard?.name || '',
      first_name: first,
      last_name: last,
      card_number: '',
      expiration_month: expMonth,
      expiration_year: expYear,
      cvv: '',
      card_type: '',
    });
  }, [opened]);

  const handleClose = () => {
    dispatch(closeDialog({ dialog: DIALOGS.retryUserPayment }));
  };

  const getMonth = (value: string) => {
    if (Number(value) === 0) return value;
    if (Number(value) < 10 && value.length < 2) return `0${value}`;
    return value;
  };

  const onChange = (value: string | undefined, field: string) => {
    if (value !== undefined) {
      switch (field) {
        case 'card_number':
          if (value.length > 16) return;
          break;
        case 'expiration_month':
          if (value.length > 2) return;
          if (Number(value) > 12) return;
          setCardState({ ...cardState, expiration_month: getMonth(value) });
          return;
        case 'expiration_year':
          if (value.length > 2) return;
          break;
        case 'cvv':
          if (value.length > 4) return;
          break;
        default:
      }
    }
    setCardState({ ...cardState, [field]: value });
  };

  const validateCard = (): boolean => {
    dispatch(setError({ [FORM.edit_creditcard]: {} }));
    try {
      CreditCardValidator(lang, false).validateSync(cardState, { abortEarly: false });
    } catch (validationErrors: any) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: t(lang, 'forms.creditcards.validation_errors'),
      }));
      const errorBag = {} as ErrorBag;
      validationErrors.inner.forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
      dispatch(setError({ [FORM.edit_creditcard]: errorBag }));
      return false;
    }
    return true;
  };

  const buildInvoiceItems = (pmnt: any) => {
    const invoices = pmnt.invoices ?? [];
    const totalAmount = Number(pmnt.amount) ?? 0;
    if (invoices.length === 1) {
      return [{ invoice_id: invoices[0].id, amount: totalAmount }];
    }
    let remaining = totalAmount;
    return invoices.reduce((items: any[], invoice: any) => {
      if (remaining <= 0) return items;
      const allocated = (invoice.balance ?? 0) < remaining ? invoice.balance : remaining;
      remaining -= allocated;
      items.push({ invoice_id: invoice.id, amount: allocated });
      return items;
    }, []);
  };

  const handleConfirm = async () => {
    if (!payment) return;

    // If user edited the card, validate and save first
    if (editCard && creditCard && payment.payment_type === PAYMENT_TYPE.cc) {
      if (!validateCard()) return;

      const payload: CreditCardPayload = {
        ...cardState,
        contact_id: contactId,
        card_type: getCardType(cardState.card_number) || creditCard.card_holder,
      };

      await dispatch(putContactCreditCardAction(payload, creditCard.id));
    }

    // Retry the payment
    dispatch(putPaymentAction(payment.id, {
      ...payment,
      payment_status: PAYMENT_STATUS.scheduled,
      scheduled_date: moment().startOf('day'),
      paid_date: null,
      processed_date: null,
      invoices: buildInvoiceItems(payment),
    }));

    // Refresh the payments list and credit cards
    dispatch(getEntitiesAction(ENTITIES.payments));
    dispatch(getUserCreditCardAction());

    handleClose();
  };

  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'CAD',
  });

  const isCC = payment?.payment_type === PAYMENT_TYPE.cc;

  return (
    <Dialog
      maxWidth="sm"
      aria-labelledby="retry-user-payment"
      open={opened}>
      <DialogTitle id="retry-user-payment">{t(lang, 'forms.payments.retry_payment')}</DialogTitle>
      <DialogContent dividers>
        <Form maxWidth={MODALS_SIZE.large}>
          <Row>
            {t(lang, 'misc.retry_payment_confirmation')}
          </Row>
          {payment && (
            <div style={{ marginTop: '12px', marginBottom: '12px' }}>
              <div><b>Payment:</b> #{payment.id} - {payment.title}</div>
              <div><b>Amount:</b> {formatter.format(Number(payment.amount ?? 0))}</div>
              <div><b>Status:</b> {payment.payment_status}</div>
            </div>
          )}
          {isCC && creditCard && (
            <div style={{ marginTop: '12px', marginBottom: '12px' }}>
              <div>
                <b>Credit Card:</b> {creditCard.masked_card_number}
                {' '}exp: {creditCard.expiration_data}
              </div>
              {!editCard && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  style={{ marginTop: '8px' }}
                  onClick={() => setEditCard(true)}>
                  {t(lang, 'forms.creditcards.edit_credit_card')}
                </Button>
              )}
              {editCard && (
                <div style={{ marginTop: '12px' }}>
                  <FormTextField
                    form={FORM.edit_creditcard}
                    label={t(lang, 'forms.creditcards.card_number')}
                    name="card_number"
                    onChange={onChange}
                    value={cardState.card_number || ''}
                    inputProps={{ type: 'number' }}
                    required />
                  <FormTextField
                    form={FORM.edit_creditcard}
                    label={t(lang, 'forms.creditcards.name')}
                    name="name"
                    onChange={onChange}
                    value={cardState.name || ''}
                    required />
                  <FormTextField
                    form={FORM.edit_creditcard}
                    label={t(lang, 'forms.creditcards.first_name')}
                    name="first_name"
                    onChange={onChange}
                    value={cardState.first_name || ''}
                    required />
                  <FormTextField
                    form={FORM.edit_creditcard}
                    label={t(lang, 'forms.creditcards.last_name')}
                    name="last_name"
                    onChange={onChange}
                    value={cardState.last_name || ''}
                    required />
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <FormTextField
                      form={FORM.edit_creditcard}
                      label={t(lang, 'forms.creditcards.expiration_month')}
                      name="expiration_month"
                      onChange={onChange}
                      value={cardState.expiration_month || ''}
                      inputProps={{ type: 'number' }}
                      required />
                    <FormTextField
                      form={FORM.edit_creditcard}
                      label={t(lang, 'forms.creditcards.expiration_year')}
                      name="expiration_year"
                      onChange={onChange}
                      value={cardState.expiration_year || ''}
                      inputProps={{ type: 'number' }}
                      required />
                    <FormTextField
                      form={FORM.edit_creditcard}
                      label="cvv"
                      name="cvv"
                      onChange={onChange}
                      value={cardState.cvv || ''}
                      inputProps={{ type: 'number' }} />
                  </div>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    style={{ marginTop: '8px' }}
                    onClick={() => setEditCard(false)}>
                    {t(lang, 'misc.cancel')}
                  </Button>
                </div>
              )}
            </div>
          )}
          {isCC && !creditCard && (
            <div style={{ marginTop: '12px', marginBottom: '12px' }}>
              <b>Credit Card:</b> N/A
            </div>
          )}
          <BottomRow style={{ alignItems: 'top' }}>
            <Button
              variant="contained"
              color="primary"
              style={{ margin: '24px 0px 16px' }}
              onClick={handleClose}>
              {t(lang, 'misc.cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              style={{ margin: '24px 0px 16px' }}
              onClick={handleConfirm}>
              {t(lang, 'misc.retry_payments')}
            </Button>
          </BottomRow>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default RetryUserPaymentDialog;
